import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { logger } from '../utils/logger';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Doughnut } from 'react-chartjs-2';

// Register required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

// No need for explicit TypeScript declarations, we'll use type assertions instead

interface SentimentGaugeProps {
  score: number;
  sentimentLevel: {
    level: string;
    color: string;
  };
  displayMode?: 'sentiment' | 'reputation';
  animateChart?: boolean;
  size?: 'compact' | 'standard';
  isPdfMode?: boolean; 
  onGaugeRendered?: (chartName: string, imageDataUrl: string) => void;
  chartName?: string;
}

const SentimentGauge: React.FC<SentimentGaugeProps> = ({ 
  score, 
  sentimentLevel,
  displayMode = 'sentiment',
  animateChart = true,
  size = 'standard',
  isPdfMode = false,
  onGaugeRendered,
  chartName = 'reputationGauge'
}) => {
  const theme = useTheme();
  const chartRef = React.useRef<ChartJS<'doughnut'>>(null); // Ref for the Doughnut chart
  const needleCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State for animated score
  const [displayedScore, setDisplayedScore] = useState(animateChart ? 0 : score);
  
  // Function to draw the needle
  const drawNeedle = useCallback((canvas: HTMLCanvasElement, score: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const parentRect = canvas.parentElement?.getBoundingClientRect();
    if (!parentRect || parentRect.width === 0 || parentRect.height === 0) {
      return;
    }
    
    canvas.width = parentRect.width;
    canvas.height = parentRect.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Calculate needle angle based on score (0-100)
    const angle = (-180 + (score / 100) * 180) * Math.PI / 180;
    
    // For a doughnut chart with 75% cutout, the center is where the inner circle would be
    // Since it's a semi-circle (180 degrees), the center Y should account for the chart's actual position
    const centerX = canvas.width / 2;
    
    // For a Chart.js semi-circle (circumference: 180, rotation: -90), 
    // the chart is drawn with its center at the bottom edge of the visible arc
    // Chart.js positions the semi-circle to use maximum available space
    // The center is typically around 80-85% down from the top
   // const centerY = canvas.height * 0.75;
    
    
    // Calculate needle length based on container dimensions and size setting
    // Use the smaller dimension to ensure the needle fits properly
    const baseSize = Math.min(canvas.width, canvas.height * 2); // *2 because semi-circle uses half height
    
    // Different needle length calculations based on size
    let needleLength;
    // Different centerY calculations based on size
    let centerY;
    let needleWidth;
    let centerRadius;
    if (size === 'compact') {
      // For compact size (used in AnimatedSimulationDemo), use a shorter needle
      needleLength = baseSize * 0.35; // 25% of base size for compact display
      centerY = canvas.height;
      needleWidth = 0.008;
      centerRadius = 0.025;
    } else {
      // For standard size (used in SimulationResultsPage), use the original length
      needleLength = baseSize * 0.35; // 35% of base size for good proportions
      centerY = canvas.height;
      needleWidth = 0.008;
      centerRadius = 0.02;
    }
    
    // Calculate needle endpoint
    const endX = centerX + Math.cos(angle) * needleLength;
    const endY = centerY + Math.sin(angle) * needleLength;
    
    // Draw needle line
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(endX, endY);
    ctx.lineWidth = Math.max(2, canvas.width * needleWidth);
    ctx.strokeStyle = theme.palette.mode === 'dark' ? '#e6edf3' : '#333';
    ctx.stroke();
    
    // Draw center circle
    ctx.beginPath();
    const dotRadius = Math.max(4, canvas.width * centerRadius);
    ctx.arc(centerX, centerY, dotRadius, 0, 2 * Math.PI);
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#e6edf3' : '#333';
    ctx.fill();
  }, [size, theme.palette.mode]);
  
  // Animate score when animateChart becomes true
  useEffect(() => {
    if (!animateChart) {
      return;
    }
    
    const startScore = 0;
    const endScore = score;
    const duration = 1000; // 1 second animation
    const startTime = Date.now();
    
    const animateScore = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      
      const currentScore = startScore + (endScore - startScore) * easedProgress;
      setDisplayedScore(currentScore);
      
      if (progress < 1) {
        requestAnimationFrame(animateScore);
      }
    };
    
    requestAnimationFrame(animateScore);
  }, [animateChart, score]);

  // Clear canvas when not animating
  useEffect(() => {
    if (animateChart || !needleCanvasRef.current) return;
    
    const canvas = needleCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const parentRect = canvas.parentElement?.getBoundingClientRect();
    if (parentRect && parentRect.width > 0 && parentRect.height > 0) {
      canvas.width = parentRect.width;
      canvas.height = parentRect.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [animateChart]);

  // Draw needle on canvas overlay
  useEffect(() => {
    if (!needleCanvasRef.current || (animateChart && !isPdfMode) ) return; // Only draw if not animating for web or if in PDF mode
    
    const canvas = needleCanvasRef.current;
    // In PDF mode, use the final score directly. Otherwise, use the animated displayedScore.
    drawNeedle(canvas, isPdfMode ? score : displayedScore);
  }, [displayedScore, score, animateChart, isPdfMode, size, drawNeedle]); // Added drawNeedle, score, isPdfMode

  // Add ResizeObserver to handle dynamic resizing with debouncing
  useEffect(() => {
    if (!containerRef.current || !needleCanvasRef.current || !animateChart) return;
    
    let resizeTimeout: NodeJS.Timeout;
    
    const resizeObserver = new ResizeObserver(() => {
      // Debounce resize events to prevent excessive redraws
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (needleCanvasRef.current && (animateChart && !isPdfMode)) { // Only redraw dynamically if animating for web
          drawNeedle(needleCanvasRef.current, displayedScore);
        }
      }, 100); // 100ms debounce
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [displayedScore, animateChart, size, isPdfMode, drawNeedle]);

  // Get colors based on display mode
  const getGaugeColors = () => {
    if (displayMode === 'reputation') {
      // Colors for reputation: Red, Orange, Yellow, Light Green, Green
      return ['#FF6B6B', '#FF9A66', '#FFD166', '#80D6A0', '#06D6A0'];
    } else {
      // Standard sentiment colors: Red, Yellow, Green
      return ['#FF6B6B', '#FFD166', '#06D6A0'];
    }
  };

  // Get data segments based on display mode
  const getDataSegments = () => {
    if (displayMode === 'reputation') {
      // Adjust segment sizes to ensure proper needle alignment
      // Make the last segment (green) start at 80 instead of 84
      return [20, 20, 20, 20, 20]; // 5 equal segments for reputation
    } else {
      return [25, 25, 50]; // Original 3 segments for sentiment
    }
  };

  // Chart data for the gauge (semi-circle)
  const chartData = {
    datasets: [
      {
        data: (animateChart && !isPdfMode) ? getDataSegments() : getDataSegments(), // Always full segments for PDF
        backgroundColor: (animateChart && !isPdfMode) ? getGaugeColors() : getGaugeColors(), // Always real colors for PDF
        borderWidth: 0,
      },
    ],
    // Empty labels array to ensure no labels are shown
    labels: Array(getDataSegments().length).fill('') // Dynamic labels based on segments
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    circumference: 180,
    rotation: -90,
    cutout: '75%',
    animation: {
        duration: (animateChart && !isPdfMode) ? 1000 : 0, // No animation for PDF
        easing: 'easeOutCubic',
        onComplete: () => {
          if (isPdfMode && chartRef.current && needleCanvasRef.current && onGaugeRendered) {
            const gaugeBgImage = new Image();
            gaugeBgImage.onload = () => {
              const needleCanvas = needleCanvasRef.current;
              if (!needleCanvas) return;

              // Ensure needle is drawn with final score for PDF
              drawNeedle(needleCanvas, score);

              const tempCanvas = document.createElement('canvas');
              const containerWidth = containerRef.current?.offsetWidth || 300;
              const containerHeight = containerRef.current?.offsetHeight || 150; 
              tempCanvas.width = containerWidth;
              tempCanvas.height = containerHeight;
              const ctx = tempCanvas.getContext('2d');
              if (!ctx) return;

              // Draw gauge background
              ctx.drawImage(gaugeBgImage, 0, 0, tempCanvas.width, tempCanvas.height);
              // Draw needle on top (needleCanvas is already sized to its parent by drawNeedle)
              ctx.drawImage(needleCanvas, 0, 0, tempCanvas.width, tempCanvas.height);

              const compositeImageDataUrl = tempCanvas.toDataURL('image/png');
              onGaugeRendered(chartName, compositeImageDataUrl);
            };
            gaugeBgImage.onerror = () => {
              logger.error("Failed to load gauge background image for PDF rendering.");
              // Optionally callback with an error or a placeholder
            };
            gaugeBgImage.src = chartRef.current.toBase64Image();
          }
        }
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const
    },
    plugins: {
      tooltip: { enabled: false },
      legend: { display: false },
      datalabels: {
        display: false // Explicitly disable data labels
      }
    },
    // Improve performance on mobile
    devicePixelRatio: typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1,
    // Prevent layout shifts
    layout: {
      padding: 0
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      position: 'relative',
      // Prevent layout shifts
      minHeight: size === 'compact' ? '120px' : '160px',
      maxHeight: size === 'compact' ? '180px' : '240px'
    }}>
      {/* Gauge Chart with Needle Overlay */}
      <Box 
        ref={containerRef}
        sx={{ 
          width: '100%',
          height: '70%', // Use percentage instead of flexGrow for more predictable sizing
          minHeight: size === 'compact' ? '80px' : '120px',
          maxHeight: size === 'compact' ? '120px' : '180px',
          position: 'relative',
          mb: 1,
          // Prevent overflow and layout shifts
          overflow: 'hidden'
        }}
      >
        {/* Doughnut Chart */}
        <Doughnut 
          data={chartData} 
          options={chartOptions as any} 
          key={animateChart ? 'animated' : 'static'}
          ref={chartRef}
        />
        
        {/* Needle Canvas Overlay */}
        <Box
          component="canvas"
          ref={needleCanvasRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        />
      </Box>
      
      {/* Score Value - Responsive and constrained */}
      <Box sx={{ 
        height: '30%',
        minHeight: size === 'compact' ? '30px' : '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 'bold', 
            lineHeight: 1,
            fontSize: size === 'compact' 
              ? { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' }
              : { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            // Prevent text from causing layout shifts
            textAlign: 'center',
            whiteSpace: 'nowrap'
          }}
        >
          {Math.round(displayedScore)}
        </Typography>
      </Box>
    </Box>
  );
};

export default SentimentGauge; 
